import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export interface Announcement {
  id: string;
  title: string;
  content: string;
  type: 'update' | 'feature' | 'tip' | 'welcome' | 'highlight';
  video_url: string | null;
  image_url: string | null;
  button_text: string | null;
  button_link: string | null;
  order_position: number;
  is_featured: boolean;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  created_by: string | null;
}

export interface CreateAnnouncementData {
  title: string;
  content: string;
  type: 'update' | 'feature' | 'tip' | 'welcome' | 'highlight';
  video_url?: string | null;
  image_url?: string | null;
  button_text?: string | null;
  button_link?: string | null;
  order_position?: number;
  is_featured?: boolean;
  is_active?: boolean;
}

export const useAnnouncements = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: announcements, isLoading } = useQuery({
    queryKey: ['announcements'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('platform_announcements')
        .select('*')
        .order('order_position', { ascending: true });

      if (error) throw error;
      return data as Announcement[];
    },
  });

  const createMutation = useMutation({
    mutationFn: async (data: CreateAnnouncementData) => {
      const { data: user } = await supabase.auth.getUser();
      
      const { error } = await supabase
        .from('platform_announcements')
        .insert({
          ...data,
          created_by: user.user?.id,
        });

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['announcements'] });
      toast({
        title: "Anúncio criado",
        description: "O anúncio foi criado com sucesso.",
      });
    },
    onError: (error) => {
      console.error("Erro ao criar anúncio:", error);
      toast({
        title: "Erro ao criar",
        description: "Não foi possível criar o anúncio.",
        variant: "destructive",
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, ...data }: Partial<Announcement> & { id: string }) => {
      const { error } = await supabase
        .from('platform_announcements')
        .update(data)
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['announcements'] });
      toast({
        title: "Anúncio atualizado",
        description: "O anúncio foi atualizado com sucesso.",
      });
    },
    onError: (error) => {
      console.error("Erro ao atualizar anúncio:", error);
      toast({
        title: "Erro ao atualizar",
        description: "Não foi possível atualizar o anúncio.",
        variant: "destructive",
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('platform_announcements')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['announcements'] });
      toast({
        title: "Anúncio excluído",
        description: "O anúncio foi excluído com sucesso.",
      });
    },
    onError: (error) => {
      console.error("Erro ao excluir anúncio:", error);
      toast({
        title: "Erro ao excluir",
        description: "Não foi possível excluir o anúncio.",
        variant: "destructive",
      });
    },
  });

  const highlightAnnouncements = announcements?.filter(a => a.type === 'highlight' && a.is_active) || [];
  const featuredAnnouncements = announcements?.filter(a => a.is_featured && a.type !== 'highlight') || [];
  const regularAnnouncements = announcements?.filter(a => !a.is_featured && a.type !== 'highlight') || [];

  return {
    announcements,
    highlightAnnouncements,
    featuredAnnouncements,
    regularAnnouncements,
    isLoading,
    createAnnouncement: createMutation.mutate,
    updateAnnouncement: updateMutation.mutate,
    deleteAnnouncement: deleteMutation.mutate,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
  };
};
